/**
 * One access case (5 Oct): each item with its team, status and approvals,
 * and for a New case the account gate. 6 Oct (product owner): one page per
 * person, so no "Open the new starter" button; `/starters/<id>` redirects here.
 *
 * 5 Oct, product owner's UAT: the header says the case's stage as the list
 * does, and who it waits on; each item row says what it waits on. All the
 * buttons (Open and complete, Approve / Reject, Confirm) are here.
 *
 * 22 Oct (Rusty, demo case 11150): each step's button only for whoever
 * completes it (the row's `action_for_you`); and "Add a comment" under the
 * items, for anyone who sees the case.
 *
 * 29 Oct (Rusty: "map out the process"): the case's step bar under the
 * header, and the item tickets as a tree under it, each with its own steps.
 */

import { Alert, Badge, Typography } from "@norican/react";
import { useEffect } from "react";
import { useLocation, useParams } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingState,
  PageContainer,
  SectionCard,
  useDateFormatter,
} from "@/components/common";
import { StarterAccountGate } from "@/features/starters";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import type { AccessCase } from "@/lib/api";

import { useAccessCase } from "./api";
import { useAccessHome } from "./components/access-home";
import { AccessItemRow } from "./components/access-item-row";
import { CaseActivity } from "./components/case-activity";
import { JourneyBar } from "./components/journey-bar";
import { StatusBadge } from "./components/status-badge";
import { useWaitingWords } from "./components/waiting-on";

export function AccessCasePage() {
  const { caseId = "" } = useParams<{ caseId: string }>();
  const { t } = useT("tickets");
  const query = useAccessCase(caseId);
  const item = query.data;
  const words = useWaitingWords();
  const home = useAccessHome();
  useDocumentTitle(item?.person_name ?? t("access.title"));
  const { hash } = useLocation();
  useScrollToItem(item !== undefined);
  const breadcrumbs = (
    <AppBreadcrumbs
      items={[{ label: home.label, to: home.to }, { label: item?.person_name ?? "…" }]}
    />
  );
  if (query.isPending) {
    return (
      <PageContainer title={t("access.title")} breadcrumbs={breadcrumbs}>
        <LoadingState />
      </PageContainer>
    );
  }
  if (query.isError || item === undefined) {
    return (
      <PageContainer title={t("access.title")} breadcrumbs={breadcrumbs}>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </PageContainer>
    );
  }
  return (
    <PageContainer
      title={item.person_name}
      subtitle={`${t(`access.type.${item.type}`)} · ${t("access.caseNumber", { number: item.case_number })}`}
      breadcrumbs={breadcrumbs}
    >
      <div className="flex flex-col gap-6">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge domain="stage" status={item.stage ?? "in_progress"} size="sm">
              {t(`access.stage.${item.stage ?? "in_progress"}`)}
            </StatusBadge>
            {(item.action_count ?? 0) > 0 ? (
              <Badge tone="accent" variant="solid" size="sm">
                {t("access.actionForYouCount", { n: item.action_count ?? 0 })}
              </Badge>
            ) : null}
          </span>
          {item.archived ? null : (
            <Typography as="p" variant="body-sm" className="break-words">
              {words.caseLine(item.waiting_on ?? [])}
            </Typography>
          )}
        </div>
        {(item.journey ?? []).length > 0 ? (
          <SectionCard title={t("access.journey.title", { number: item.case_number })}>
            <JourneyBar steps={item.journey ?? []} />
          </SectionCard>
        ) : null}
        {item.read_only ? <Alert tone="neutral">{t("access.readOnly")}</Alert> : null}
        {item.requested_by?.display_name || item.manager?.display_name ? (
          <Typography as="p" variant="body-sm">
            {[
              item.requested_by?.display_name
                ? t("access.requestedBy", { name: item.requested_by.display_name })
                : null,
              // New: the manager follows the case (named on the NUR, 5 Oct).
              item.manager?.display_name
                ? t("access.manager", { name: item.manager.display_name })
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        ) : null}
        {item.type === "leave" ? <LeaveLine item={item} /> : null}
        {item.starter_path && !item.read_only ? (
          // 19 Oct (finding 9): Global IT's "Confirm the person" lands here.
          <div id="account" className="scroll-mt-4">
            <StarterAccountGate caseId={starterCaseId(item.starter_path)} />
          </div>
        ) : null}
        <SectionCard title={t("access.items")}>
          {/* 29 Oct: the item tickets hang under the case's request, a tree. */}
          <ul className="border-border m-0 ml-1 flex list-none flex-col gap-4 border-l-2 py-0 pr-0 pl-4">
            {(item.items ?? []).map((entry) => (
              <li key={`${entry.n}-${entry.item}`} id={`item-${entry.n}`} className="scroll-mt-4">
                <AccessItemRow
                  caseId={item.case_id}
                  item={entry}
                  personName={item.person_name}
                  detailed
                  readOnly={item.read_only === true}
                  leavingDate={item.leaving_date ?? null}
                  access={item}
                  focused={hash === `#item-${entry.n}`}
                />
              </li>
            ))}
          </ul>
        </SectionCard>
        <CaseActivity caseId={item.case_id} />
      </div>
    </PageContainer>
  );
}

/**
 * 13 Oct: the approval email (Ivanti, rule N1) links to "/access/<case>#item-<n>",
 * the item the approver decides. The rows render after the case loads, so
 * the browser's own jump to the anchor misses them; this scrolls once they are there.
 */
function useScrollToItem(loaded: boolean) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!loaded || !(hash.startsWith("#item-") || hash === "#account")) {
      return;
    }
    const target = document.getElementById(hash.slice(1));
    // 19 Oct (finding 10): an item with a decision for the reader focuses
    // that decision's heading instead (the card scrolls itself to it).
    if (target === null || target.querySelector("[data-approval-heading]") !== null) {
      return;
    }
    target.scrollIntoView({ block: "start" });
  }, [loaded, hash]);
}

/**
 * Leave (6 Oct, decision 10): the leaving date. 28 Oct (Rusty): only the
 * date; HR validation is retired (every leaver comes from the HR Portal), so
 * an HR validator shows only while one is actually named.
 */
function LeaveLine({ item }: { item: AccessCase }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const parts = [
    item.leaving_date
      ? t("access.leavingDate", { date: formatDate(new Date(`${item.leaving_date}T12:00:00`)) })
      : null,
    !item.hr_portal && !item.valid_as_raised && item.hr_validator
      ? t("access.leave.validator", { name: item.hr_validator })
      : null,
  ].filter(Boolean);
  if (parts.length === 0) {
    return null;
  }
  return (
    <Typography as="p" variant="body-sm">
      {parts.join(" · ")}
    </Typography>
  );
}

/** The NUR's case id from `/starters/<id>`. */
function starterCaseId(path: string): string {
  return decodeURIComponent(path.slice(path.lastIndexOf("/") + 1));
}
